// Tedarikci duyurusu (Rexven: "REX-1857 telif riski nedeniyle kaldirildi",
// "REX-199 fiyati guncellendi"). Metin yapistirilir, SKU'lar ve eylem tahmin
// edilir; kullanici her SKU icin eylemi onaylar.
//   Kaldir  → katalogda "kaldirildi" isareti, bagli Etsy ilanlari PASIFE, taslak arsiv
//   Fiyat   → "fiyat guncellenecek" isareti; eklenti Rexven'den yeniden ceker,
//             fiyat Listing Builder > "Fiyatlari guncelle" ile onaylanip Etsy'ye yazilir

import { useState } from "react";
import { X, Loader2, Megaphone, AlertTriangle, CheckCircle2 } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

type Action = "discontinue" | "price" | "skip";
type Lookup = {
  products: Array<{ sku: string; catalogName: string; name: string; image: string | null; discontinued?: { at: string }; priceUpdate?: { requestedAt: string; fetchedAt?: string } }>;
  listings: Array<{ draftId: string; title: string; status: string; sku: string; etsy: Array<{ shopId: string; listingId: string }> }>;
};

const ACTION_LABEL: Record<Action, string> = { discontinue: "Kaldır (Etsy'de pasife al)", price: "Fiyat güncellenecek", skip: "Yok say" };

/** Duyurudaki SKU'lar ve her birinin gectigi satira gore tahmini eylem */
export function parseNotice(text: string): Array<{ sku: string; action: Action; context: string }> {
  const out = new Map<string, { sku: string; action: Action; context: string }>();
  for (const line of text.split(/\n+/)) {
    const skus = line.match(/REX-\d+/gi);
    if (!skus) continue;
    const l = line.toLocaleLowerCase("tr");
    const action: Action = /kald[ıi]r|yay[ıi]ndan|telif|removed|discontinu/.test(l) ? "discontinue" : /fiyat|price/.test(l) ? "price" : "skip";
    for (const s of skus) {
      const sku = s.toUpperCase();
      if (!out.has(sku)) out.set(sku, { sku, action, context: line.trim().slice(0, 200) });
    }
  }
  return [...out.values()];
}

export function SupplierNoticeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState<Array<{ sku: string; action: Action; context: string }> | null>(null);
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ marked: number; drafts: number; deactivated: number; failures: string[]; price: number } | null>(null);

  const post = async (path: string, body: unknown) => {
    const r = await fetch(`${getBackendUrl()}/api/supplier-catalogs/supplier-notice/${path}`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
    return j.data;
  };

  async function analyze() {
    const parsed = parseNotice(text);
    if (!parsed.length) { setError("Metinde REX-… biçiminde SKU bulunamadı."); return; }
    setBusy(true); setError(null);
    try {
      setLookup(await post("lookup", { skus: parsed.map((p) => p.sku) }));
      setRows(parsed);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function apply() {
    if (!rows) return;
    const disc = rows.filter((r) => r.action === "discontinue");
    const etsyCount = lookup?.listings.filter((l) => disc.some((d) => d.sku.toLowerCase() === l.sku)).reduce((n, l) => n + l.etsy.length, 0) ?? 0;
    if (disc.length && !confirm(`${disc.length} ürün kaldırıldı olarak işaretlenecek ve ${etsyCount} Etsy ilanı pasife alınacak. Devam edilsin mi?`)) return;
    setBusy(true); setError(null);
    try {
      const price = rows.filter((r) => r.action === "price").map((r) => r.sku);
      // Her kaldirma kendi duyuru satirini gerekce olarak tasir
      const sum = { marked: 0, drafts: 0, deactivated: 0, failures: [] as string[] };
      const groups = [...new Set(disc.map((r) => r.context))].map((context) => ({ context, skus: disc.filter((r) => r.context === context).map((r) => r.sku) }));
      for (const [i, g] of (groups.length ? groups : [{ context: "", skus: [] as string[] }]).entries()) {
        const d = await post("apply", { discontinue: g.skus, priceUpdate: i === 0 ? price : [], reason: g.context });
        sum.marked += d.marked; sum.drafts += d.drafts; sum.deactivated += d.deactivated; sum.failures.push(...d.failures);
      }
      setResult({ ...sum, price: price.length });
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Megaphone className="h-4 w-4 text-amber-600" /> Tedarikçi duyurusu</h3>
          <button onClick={onClose} disabled={busy} aria-label="Kapat" className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4 text-sm">
          {result ? (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" />
                {result.marked} ürün işaretlendi · {result.drafts} taslak arşivlendi · {result.deactivated} Etsy ilanı pasife alındı.</p>
              {result.price > 0 && (
                <p className="rounded-lg bg-amber-50 p-3 text-amber-800">
                  {result.price} ürünün fiyatı güncellenecek. Eklentide <b>Rexven</b> sekmesindeki <b>“Fiyatı güncellenecekler”</b> listesinden
                  yeni maliyeti çekin; sonra Listing Builder'da <b>“Fiyatları güncelle”</b> ile yeni Etsy fiyatlarını onaylayın.
                </p>
              )}
              {result.failures.length > 0 && (
                <div className="rounded-lg bg-red-50 p-3 text-red-700">
                  <p className="mb-1 font-medium">Pasife alınamayan ilanlar:</p>
                  {result.failures.map((f) => <p key={f} className="text-xs">{f}</p>)}
                </div>
              )}
            </div>
          ) : !rows ? (
            <>
              <p className="text-gray-600">Duyuru metnini yapıştırın. İçindeki REX-… SKU'ları bulunur; satırında “kaldırıldı/telif” geçenler <b>Kaldır</b>, “fiyat” geçenler <b>Fiyat güncellenecek</b> olarak önerilir.</p>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
                placeholder="Örn: REX-1857 ve REX-1860 telif riski nedeniyle kaldırılmıştır. REX-199 fiyatı güncellenmiştir."
                className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </>
          ) : (
            <div className="space-y-3">
              {rows.map((r, i) => {
                const prods = lookup?.products.filter((p) => p.sku === r.sku.toLowerCase()) ?? [];
                const lists = lookup?.listings.filter((l) => l.sku === r.sku.toLowerCase()) ?? [];
                return (
                  <div key={r.sku} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                      {prods[0]?.image && <img src={prods[0].image} alt="" className="h-12 w-12 rounded object-cover" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900">{r.sku} {prods[0] ? <span className="font-normal text-gray-600">· {prods[0].name}</span> : <span className="font-normal text-gray-400">· katalogda yok</span>}</p>
                        <p className="text-xs text-gray-500">
                          {prods.map((p) => p.catalogName).join(", ") || "—"} · {lists.length} taslak · {lists.reduce((n, l) => n + l.etsy.length, 0)} Etsy ilanı
                          {prods.some((p) => p.discontinued) && <span className="ml-1 text-red-600">· zaten kaldırıldı işaretli</span>}
                        </p>
                      </div>
                      <select value={r.action} aria-label={`${r.sku} için eylem`}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, action: e.target.value as Action } : x)))}
                        className={`rounded-lg border px-2 py-1.5 text-xs ${r.action === "discontinue" ? "border-red-200 bg-red-50 text-red-700" : r.action === "price" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-gray-200 text-gray-600"}`}>
                        {(Object.keys(ACTION_LABEL) as Action[]).map((a) => <option key={a} value={a}>{ACTION_LABEL[a]}</option>)}
                      </select>
                    </div>
                    <p className="mt-2 text-xs italic text-gray-400">“{r.context}”</p>
                    {r.action === "discontinue" && lists.length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                        {lists.map((l) => <li key={l.draftId}>• {l.title.slice(0, 80)} {l.etsy.map((e) => <span key={e.listingId} className="text-gray-400"> #{e.listingId}</span>)}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {error && <p className="flex items-center gap-1.5 text-red-600"><AlertTriangle className="h-4 w-4" /> {error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          {result ? (
            <button onClick={onClose} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Kapat</button>
          ) : !rows ? (
            <button onClick={analyze} disabled={busy || !text.trim()} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} SKU'ları bul
            </button>
          ) : (
            <>
              <button onClick={() => { setRows(null); setLookup(null); }} disabled={busy} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Geri</button>
              <button onClick={apply} disabled={busy || rows.every((r) => r.action === "skip")} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Uygula
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
